import { useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { Apple, ArrowDown, KeyRound, Search } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { comingSoonToast } from "@/components/Navbar";

const HERO_IMG = "https://images.unsplash.com/photo-1623107935331-7164fb0d6978?crop=entropy&cs=srgb&fm=jpg&q=85";

const RESTROOMS = [
    { name: "Bellagio", spot: "Main Concourse", status: "Open 24/7", dist: "0.2 MI" },
    { name: "Caesars Palace", spot: "Forum Shops L2", status: "Open 24/7", dist: "0.4 MI" },
    { name: "The Venetian", spot: "Grand Canal Shoppes", status: "Open 24/7", dist: "0.6 MI" },
    { name: "Cosmopolitan", spot: "Level 2, West End", status: "Open 24/7", dist: "0.9 MI" },
    { name: "Wynn", spot: "Esplanade Wing", status: "Open 24/7", dist: "1.7 MI" },
    { name: "Binion's", spot: "Fremont St Experience", status: "Keycard", dist: "4.8 MI" },
];

const lineVariants = {
    hidden: { y: "115%" },
    show: (i) => ({
        y: 0,
        transition: { duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.35 + i * 0.14 },
    }),
};

function FinderCard() {
    const [query, setQuery] = useState("");
    const cardRef = useRef(null);
    const rx = useMotionValue(0);
    const ry = useMotionValue(0);
    const srx = useSpring(rx, { stiffness: 120, damping: 16 });
    const sry = useSpring(ry, { stiffness: 120, damping: 16 });

    const onMove = (e) => {
        const rect = cardRef.current.getBoundingClientRect();
        const px = (e.clientX - rect.left) / rect.width - 0.5;
        const py = (e.clientY - rect.top) / rect.height - 0.5;
        ry.set(px * 10);
        rx.set(-py * 10);
    };
    const onLeave = () => {
        rx.set(0);
        ry.set(0);
    };

    const results = RESTROOMS.filter((r) =>
        r.name.toLowerCase().includes(query.toLowerCase())
    );

    return (
        <div style={{ perspective: 1000 }}>
            <motion.div
                ref={cardRef}
                onMouseMove={onMove}
                onMouseLeave={onLeave}
                style={{ rotateX: srx, rotateY: sry, transformStyle: "preserve-3d" }}
                initial={{ opacity: 0, y: 60 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 1 }}
                className="glass-panel relative w-full max-w-md rounded-2xl p-5 shadow-[0_10px_30px_-10px_rgba(0,240,255,0.15),0_0_20px_rgba(255,0,122,0.1)]"
                data-testid="hero-finder-preview-card"
            >
                <div className="mb-4 flex items-center justify-between">
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-neon-cyan">
                        App Preview
                    </span>
                    <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-neon-emerald">
                        <span className="h-1.5 w-1.5 rounded-full bg-neon-emerald animate-neon-pulse" />
                        Strip Live
                    </span>
                </div>

                <div className="mb-4 flex items-center gap-2 rounded-xl border border-slate-800 bg-[#131520] px-3 py-2.5 transition-colors duration-300 focus-within:border-neon-cyan/60">
                    <Search className="h-4 w-4 text-slate-500" />
                    <input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search the Strip…"
                        data-testid="hero-interactive-search-input"
                        className="w-full bg-transparent text-sm text-white placeholder:text-slate-600 focus:outline-none"
                    />
                </div>

                <div className="max-h-64 space-y-2 overflow-hidden">
                    {results.length === 0 && (
                        <p className="py-6 text-center font-mono text-xs text-slate-500" data-testid="hero-search-empty">
                            NO MATCHES — TRY "BELLAGIO"
                        </p>
                    )}
                    {results.map((r) => (
                        <div
                            key={r.name}
                            className="flex items-center justify-between rounded-lg border border-white/5 bg-white/[0.03] px-3 py-2.5 transition-colors duration-300 hover:border-neon-cyan/30"
                            data-testid={`hero-result-${r.name.toLowerCase().replace(/[^a-z]/g, "-")}`}
                        >
                            <div>
                                <p className="text-sm font-semibold text-white">{r.name}</p>
                                <p className="text-xs text-slate-500">{r.spot}</p>
                            </div>
                            <div className="flex items-center gap-3">
                                <span
                                    className={`flex items-center gap-1 rounded-full px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${
                                        r.status === "Keycard"
                                            ? "bg-neon-gold/10 text-neon-gold"
                                            : "bg-neon-emerald/10 text-neon-emerald"
                                    }`}
                                >
                                    {r.status === "Keycard" && <KeyRound className="h-3 w-3" />}
                                    {r.status}
                                </span>
                                <span className="font-mono text-[10px] text-slate-500">{r.dist}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </motion.div>
        </div>
    );
}

export default function Hero() {
    const sectionRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ["start start", "end start"],
    });
    const bgY = useTransform(scrollYProgress, [0, 1], [0, 160]);
    const fgY = useTransform(scrollYProgress, [0, 1], [0, -60]);

    return (
        <section
            id="top"
            ref={sectionRef}
            className="relative flex min-h-screen items-center overflow-hidden pt-28 pb-20"
            data-testid="hero-section"
        >
            <motion.div style={{ y: bgY }} className="absolute inset-0" aria-hidden="true">
                <img
                    src={HERO_IMG}
                    alt=""
                    className="h-[115%] w-full object-cover opacity-35"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#06070B]/70 via-[#06070B]/85 to-[#06070B]" />
            </motion.div>
            <div className="grid-bg absolute inset-0" aria-hidden="true" />
            <div
                className="absolute -top-32 left-1/4 h-96 w-96 rounded-full bg-neon-magenta/15 blur-[140px]"
                aria-hidden="true"
            />
            <div
                className="absolute bottom-0 right-1/4 h-96 w-96 rounded-full bg-neon-cyan/15 blur-[140px]"
                aria-hidden="true"
            />

            <motion.div
                style={{ y: fgY }}
                className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-2"
            >
                <div>
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.25, duration: 0.8 }}
                        className="mb-6 font-mono text-xs uppercase tracking-[0.3em] text-neon-cyan"
                        data-testid="hero-kicker"
                    >
                        ◆ Official Support — Las Vegas Restroom Locator
                    </motion.p>

                    <h1 className="font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl">
                        <span className="block overflow-hidden pb-1">
                            <motion.span custom={0} variants={lineVariants} initial="hidden" animate="show" className="block">
                                WHEN NATURE
                            </motion.span>
                        </span>
                        <span className="block overflow-hidden pb-1">
                            <motion.span custom={1} variants={lineVariants} initial="hidden" animate="show" className="block text-neon-cyan neon-cyan">
                                CALLS ON THE STRIP,
                            </motion.span>
                        </span>
                        <span className="block overflow-hidden pb-2">
                            <motion.span custom={2} variants={lineVariants} initial="hidden" animate="show" className="block text-neon-magenta neon-magenta">
                                WE ANSWER.
                            </motion.span>
                        </span>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                        className="mt-6 max-w-lg text-base leading-relaxed text-slate-400 sm:text-lg"
                        data-testid="hero-subcopy"
                    >
                        The iOS app that maps every public restroom from Fremont Street to
                        Mandalay Bay. Questions, corrections, or a restroom we missed —
                        this is the place to get help.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.15, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                        className="mt-9 flex flex-wrap items-center gap-4"
                    >
                        <button
                            onClick={comingSoonToast}
                            data-testid="hero-app-store-cta"
                            className="group flex items-center gap-3 rounded-full bg-white px-6 py-3.5 text-sm font-bold text-[#06070B] transition-all duration-300 hover:bg-neon-cyan hover:shadow-[0_0_40px_rgba(0,240,255,0.5)]"
                        >
                            <Apple className="h-5 w-5" />
                            <span className="text-left leading-tight">
                                <span className="block text-[10px] font-medium uppercase tracking-wider opacity-60">
                                    Coming soon on the
                                </span>
                                App Store
                            </span>
                        </button>
                        <button
                            onClick={() => scrollToId("#support")}
                            data-testid="hero-get-support-cta"
                            className="flex items-center gap-2 rounded-full border border-white/15 px-6 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:border-neon-magenta/60 hover:text-neon-magenta hover:shadow-[0_0_30px_rgba(255,0,122,0.3)]"
                        >
                            Get Support
                            <ArrowDown className="h-4 w-4" />
                        </button>
                    </motion.div>
                </div>

                <div className="flex justify-center lg:justify-end">
                    <FinderCard />
                </div>
            </motion.div>
        </section>
    );
}
